/**
 * Solution Visual Studio share entrypoint.
 *
 * Mounts the PIN-gated share viewer surface. Wraps the share router with
 * QueryClientProvider so all viewer hooks (useShareLinkMeta, useShareUnlock,
 * useShareSnapshot) have a query client available.
 *
 * Security invariants:
 *   - Only ShareRouterProvider (SHARE_ROUTES) is mounted here.
 *   - No editor component is imported or reachable from this entrypoint.
 *   - The PIN gate is the first route; snapshot content is never fetched
 *     before a successful unlock (SEC-04, VIEW-01).
 *
 * Requirements: GOV-03, SEC-01, SEC-04, VIEW-01, VIEW-07
 */

import React from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { ShareRouterProvider } from "@/client/router";
import "@/client/index.css";

// ---------------------------------------------------------------------------
// QueryClient — shared instance for the share surface
// ---------------------------------------------------------------------------

const shareQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Retry once; share API errors (revoked, not found) should surface fast.
      retry: 1,
      // Share link meta: 60 s stale time — individual hooks override as needed.
      staleTime: 60_000,
    },
  },
});

// ---------------------------------------------------------------------------
// Root component
// ---------------------------------------------------------------------------

function SolutionVisualStudioShareApp() {
  return (
    <React.StrictMode>
      <QueryClientProvider client={shareQueryClient}>
        <ShareRouterProvider />
      </QueryClientProvider>
    </React.StrictMode>
  );
}

// ---------------------------------------------------------------------------
// Mount
// ---------------------------------------------------------------------------

const shareRoot =
  document.getElementById("solution-visual-studio-share-root") ??
  document.getElementById("root");

if (!shareRoot) {
  throw new Error("Solution Visual Studio share root element not found.");
}

createRoot(shareRoot).render(<SolutionVisualStudioShareApp />);
